import { BaseAjaxService } from "./shared/BaseAjaxService.js";
import { PriceCalculator } from "./shared/PriceCalculator.js";
import { NotificationManager } from "./shared/NotificationManager.js";

export default function packageSelection() {
  const isPresent = document.querySelector("[data-package-selection-wrapper]");
  if (!isPresent) {
    return;
  }

  const elements = {
    packageButtons: document.querySelectorAll(".resident-package-select"),
    allItems: document.querySelectorAll(".single-interior-item"),
    selectionButtons: document.querySelectorAll(".select-item-button"),
    body: document.body,
  };

  const ajaxService = new BaseAjaxService();
  const priceCalculator = new PriceCalculator();
  const notificationManager = new NotificationManager();
  let packageSwitchInProgress = false;

  init();

  function init() {
    setupEventListeners();
    // Always disable individual items for Mugav package residents
    setPackageDisabledState(true);
  }

  function setupEventListeners() {
    elements.packageButtons.forEach((button, index) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const packageType = getPackageTypeFromButton(button);
        if (packageType) {
          selectPackage(packageType, button);
        } else {
        }
      });
    });
  }

  function getPackageTypeFromButton(button) {
    const explicitKey = button.getAttribute("data-package-key");
    if (explicitKey) return explicitKey.toLowerCase();
    if (button.hasAttribute("data-package-minimal")) return "minimal";
    if (button.hasAttribute("data-package-urban")) return "urban";
    if (button.hasAttribute("data-package-nordic")) return "nordic";
    if (button.hasAttribute("data-package-terracota")) return "terracota";
    if (button.hasAttribute("data-package-navy")) return "navy";
    if (button.hasAttribute("data-package-clear")) return "clear";
    return null;
  }

  async function selectPackage(packageType, clickedButton) {
    if (packageSwitchInProgress) {
      return;
    }
    packageSwitchInProgress = true;

    try {
      // Show loading state
      elements.packageButtons.forEach((btn) => {
        btn.disabled = true;
      });
      elements.body.classList.add("package-selecting");

      // Handle clear package selection
      if (packageType === "clear") {
        // Send AJAX request to clear package
        const response = await ajaxService.request("clear_package");

        if (response) {
          // Clear all selections
          clearAllSelections();

          // Update package disabled state to hide all buttons for Mugav packages
          setPackageDisabledState(true);

          // Refresh display from database
          await refreshDisplayFromDatabase();

          // Trigger price recalculation
          priceCalculator.updateTotalPrice();

          // Trigger package validation update
          triggerPackageValidationUpdate();

          // Show success notification
          notificationManager.showMainNotification(
            "Sinu paketi valik on tühistatud"
          );
        }
        return;
      }

      // Clear all existing selections first
      clearAllSelections();

      // Collect all items that belong to this package
      const packageItems = [];

      elements.allItems.forEach((item) => {
        const itemDataButton = item.querySelector(".select-item-button");
        if (!itemDataButton) return;

        const itemDataJson = itemDataButton.getAttribute("data-item");
        if (!itemDataJson) return;

        try {
          const itemData = JSON.parse(itemDataJson);

          // Check if this item belongs to the selected package
          if (itemData.packages && itemData.packages.includes(packageType)) {
            // Add pricing information
            const priceAttribute = `data-plan-price-hind`; // For resident apartments
            const priceValue = item.getAttribute(priceAttribute);
            if (priceValue) {
              itemData.price = parseFloat(priceValue) || 0;
            }

            // Add category information from DOM
            itemData.main_cat =
              item.getAttribute("data-main-cat") || "Uncategorized";
            itemData.main_cat_slug =
              item.getAttribute("data-main-cat-slug") || "uncategorized";
            itemData.sub_cat =
              item.getAttribute("data-sub-cat") || "Uncategorized";

            packageItems.push(itemData);
          }
        } catch (e) {}
      });

      if (packageItems.length === 0) {
        throw new Error(`No items found for ${packageType} package`);
      }

      // Send AJAX request
      const response = await ajaxService.request("select_package", {
        package_type: packageType,
        items: JSON.stringify(packageItems),
      });

      if (response && response.selected_count !== undefined) {
        // Update button states - add selected class to active button
        updatePackageButtonStates(clickedButton, packageType);

        // Fetch saved items from database and update display/button states
        await refreshDisplayFromDatabase();

        // Update package disabled state to show selected buttons for Mugav packages
        setPackageDisabledState(true);

        // Trigger price recalculation
        priceCalculator.updateTotalPrice();

        // Trigger package validation update
        triggerPackageValidationUpdate();

        // Switch to first peakategooria view for Mugav residents
        switchToFirstPeakategoriaView();
      }
    } catch (error) {
      notificationManager.showMainNotification(
        `Error selecting ${packageType} package: ${error.message}`
      );
    } finally {
      // Reset button state
      elements.packageButtons.forEach((btn) => {
        btn.disabled = false;
      });
      elements.body.classList.remove("package-selecting");
      packageSwitchInProgress = false;
    }
  }

  function clearAllSelections() {
    // Check if this is a Mugav package
    const isMugavPackage = document.querySelector('[data-package-validation-required-text]') !== null;

    // Clear UI state for all items
    elements.allItems.forEach((itemEl) => {
      const itemButton = itemEl.querySelector(".select-item-button");
      if (itemButton) {
        itemButton.classList.remove("selected");
        itemButton.textContent = "Vali";

        // For Mugav packages, immediately hide the button to prevent flash
        if (isMugavPackage) {
          itemButton.style.opacity = "0";
          itemButton.style.pointerEvents = "none";
        }
      }
    });

    // Clear all selected item displays using the same approach
    updateInteriorSelectionDisplay([]);

    // Clear package button states
    elements.packageButtons.forEach((btn) => {
      btn.classList.remove("active", "selected");
    });
  }

  function updateUIAfterPackageSelection(packageItems, packageType) {
    // Update individual item buttons to show as selected
    packageItems.forEach((packageItem) => {
      elements.allItems.forEach((itemEl) => {
        const itemButton = itemEl.querySelector(".select-item-button");
        if (!itemButton) return;

        const itemDataJson = itemButton.getAttribute("data-item");
        if (!itemDataJson) return;

        try {
          const itemData = JSON.parse(itemDataJson);

          const sameId =
            itemData.interior_id &&
            packageItem.interior_id &&
            itemData.interior_id === packageItem.interior_id;
          const sameName = itemData.nimetus === packageItem.nimetus;
          if (sameId || sameName) {
            // Update button state
            itemButton.classList.add("selected");
            itemButton.textContent = "Valitud";
          }
        } catch (e) {}
      });
    });
  }

  function updatePackageButtonStates(activeButton, packageType) {
    // Remove active state from all package buttons
    elements.packageButtons.forEach((btn) => {
      btn.classList.remove("active", "selected");
    });

    // Add active class to clicked button
    activeButton.classList.add("active");
  }

  function updateInteriorSelectionDisplay(packageItems) {
    // Clear all existing displays first - use the BaseSelectionManager approach
    const allSelectedItemContainers = document.querySelectorAll(
      "[data-list-item-selected-items]"
    );
    allSelectedItemContainers.forEach((container) => {
      container.innerHTML = "";
    });

    // Group items by main category and sub category
    const itemsByMainCat = {};
    packageItems.forEach((item) => {
      const mainCatSlug = item.main_cat_slug || "uncategorized";
      const subCat = item.sub_cat || "Uncategorized";

      if (!itemsByMainCat[mainCatSlug]) {
        itemsByMainCat[mainCatSlug] = {};
      }
      if (!itemsByMainCat[mainCatSlug][subCat]) {
        itemsByMainCat[mainCatSlug][subCat] = [];
      }
      itemsByMainCat[mainCatSlug][subCat].push(item);
    });

    // Update each category display using BaseSelectionManager structure
    Object.entries(itemsByMainCat).forEach(([mainCatSlug, subCategories]) => {
      const categoryContainer = document.querySelector(
        `[data-list-item-with-cat="${mainCatSlug}"]`
      );
      if (!categoryContainer) return;

      const selectedItemsContainer = categoryContainer.querySelector(
        "[data-list-item-selected-items]"
      );
      if (!selectedItemsContainer) return;

      // Create subcategory groups exactly like BaseSelectionManager does
      Object.entries(subCategories).forEach(([subCat, items]) => {
        const subCategoryGroup = createSubCategoryGroup(subCat);
        selectedItemsContainer.appendChild(subCategoryGroup);

        const subCategoryList = subCategoryGroup.querySelector("ul");

        // Add each item to the subcategory list
        items.forEach((item) => {
          const listItem = document.createElement("li");
          listItem.setAttribute("data-item-name", sanitizeTitle(item.nimetus));
          listItem.textContent = getItemDisplayName(item);
          subCategoryList.appendChild(listItem);
        });
      });

      // Show clear button for this category if it has items
      const clearButton = categoryContainer.querySelector(
        "[data-clear-choices]"
      );
      if (clearButton && Object.keys(subCategories).length > 0) {
        clearButton.classList.remove("hidden");
      }
    });
  }

  function createSubCategoryGroup(subCat) {
    const subCategoryGroup = document.createElement("div");
    subCategoryGroup.classList.add("selected-items-subcategory");
    subCategoryGroup.setAttribute("data-sub-cat-group", sanitizeTitle(subCat));
    subCategoryGroup.innerHTML = `<span class="block w-full font-medium mt-6">${subCat}:</span><ul class="text-13"></ul>`;
    return subCategoryGroup;
  }

  async function refreshDisplayFromDatabase() {
    try {
      // Fetch selected items from database using the simple endpoint
      const response = await ajaxService.request("get_selected_items_resident");

      if (response && response.selected_items) {
        // Sync button states from persisted rows first to avoid local UI drift
        syncButtonsFromDatabase(response.selected_items);

        // Clear existing displays
        clearAllSelectionDisplays();

        // Update display with items from database
        updateDisplayFromDatabaseItems(response.selected_items);

        // Show clear buttons for categories with items
        updateClearButtonsVisibility();
      } else {
      }
    } catch (error) {}
  }

  function syncButtonsFromDatabase(selectedItems) {
    const selectedById = new Set();
    const selectedByName = new Set();

    selectedItems.forEach((item) => {
      if (item && typeof item === "object") {
        if (item.interior_id) {
          selectedById.add(String(item.interior_id));
        }
        if (item.nimetus) {
          selectedByName.add(String(item.nimetus));
        }
      }
    });

    elements.allItems.forEach((itemEl) => {
      const itemButton = itemEl.querySelector(".select-item-button");
      if (!itemButton) return;

      const itemDataJson = itemButton.getAttribute("data-item");
      if (!itemDataJson) return;

      let isSelected = false;
      try {
        const itemData = JSON.parse(itemDataJson);
        const interiorId = itemData?.interior_id ? String(itemData.interior_id) : "";
        const name = itemData?.nimetus ? String(itemData.nimetus) : "";

        if (interiorId && selectedById.has(interiorId)) {
          isSelected = true;
        } else if (!interiorId && name && selectedByName.has(name)) {
          isSelected = true;
        }
      } catch (e) {}

      if (isSelected) {
        itemButton.classList.add("selected");
        itemButton.classList.remove("btn-deactive");
        itemButton.textContent = "Valitud";
      } else {
        itemButton.classList.remove("selected");
        itemButton.classList.add("btn-deactive");
        itemButton.textContent = "Vali";
      }
    });
  }

  function clearAllSelectionDisplays() {
    // Clear all selected item displays
    const allSelectedItemContainers = document.querySelectorAll(
      "[data-list-item-selected-items]"
    );
    allSelectedItemContainers.forEach((container, index) => {
      container.innerHTML = "";
    });

    // Hide all clear buttons
    const allClearButtons = document.querySelectorAll("[data-clear-choices]");
    allClearButtons.forEach((button) => {
      button.classList.add("hidden");
    });
  }

  function updateDisplayFromDatabaseItems(selectedItems) {
    // Group items by main category and sub category
    const itemsByMainCat = {};

    selectedItems.forEach((item) => {
      const mainCatSlug = item.main_cat_slug || "uncategorized";
      const subCat = item.sub_cat || "Uncategorized";

      if (!itemsByMainCat[mainCatSlug]) {
        itemsByMainCat[mainCatSlug] = {};
      }
      if (!itemsByMainCat[mainCatSlug][subCat]) {
        itemsByMainCat[mainCatSlug][subCat] = [];
      }
      itemsByMainCat[mainCatSlug][subCat].push(item);
    });

    // Update each category display using BaseSelectionManager structure
    Object.entries(itemsByMainCat).forEach(([mainCatSlug, subCategories]) => {
      const categoryContainer = document.querySelector(
        `[data-list-item-with-cat="${mainCatSlug}"]`
      );
      if (!categoryContainer) {
        return;
      }

      const selectedItemsContainer = categoryContainer.querySelector(
        "[data-list-item-selected-items]"
      );
      if (!selectedItemsContainer) {
        return;
      }

      // Create subcategory groups exactly like BaseSelectionManager does
      Object.entries(subCategories).forEach(([subCat, items]) => {
        const subCategoryGroup = createSubCategoryGroup(subCat);
        selectedItemsContainer.appendChild(subCategoryGroup);

        const subCategoryList = subCategoryGroup.querySelector("ul");

        // Add each item to the subcategory list
        items.forEach((item) => {
          const listItem = document.createElement("li");
          listItem.setAttribute("data-item-name", sanitizeTitle(item.nimetus));
          listItem.textContent = getItemDisplayName(item);
          subCategoryList.appendChild(listItem);
        });
      });
    });
  }

  function updateClearButtonsVisibility() {
    const allClearButtons = document.querySelectorAll("[data-clear-choices]");
    allClearButtons.forEach((button) => {
      const mainCatSlug = button.getAttribute("data-interior-cat-in-list");
      if (!mainCatSlug) return;

      const categoryContainer = document.querySelector(
        `[data-list-item-with-cat="${mainCatSlug}"]`
      );
      if (!categoryContainer) return;

      const selectedItemsContainer = categoryContainer.querySelector(
        "[data-list-item-selected-items]"
      );
      const hasSelectedItems =
        selectedItemsContainer && selectedItemsContainer.children.length > 0;

      if (hasSelectedItems) {
        button.classList.remove("hidden");
      } else {
        button.classList.add("hidden");
      }
    });
  }

  function sanitizeTitle(title) {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  function getItemDisplayName(item) {
    if (!item || typeof item !== "object") {
      return "";
    }
    return item.display_nimetus || item.nimetus || "";
  }

  function addItemToSelectedDisplay(item, categorySlug) {
    const categoryContainer = document.querySelector(
      `[data-list-item-with-cat="${categorySlug}"]`
    );
    if (!categoryContainer) return;

    const selectedItemsContainer = categoryContainer.querySelector(
      "[data-list-item-selected-items]"
    );
    if (!selectedItemsContainer) return;

    // Check if item is already displayed
    const existingItem = selectedItemsContainer.querySelector(
      `[data-selected-item="${item.nimetus}"]`
    );
    if (existingItem) return;

    // Create selected item display
    const itemElement = document.createElement("div");
    itemElement.setAttribute("data-selected-item", item.nimetus);
    itemElement.className =
      "inline-block px-8 py-4 mt-8 mr-8 bg-white border border-black text-13 rounded-4";
    itemElement.textContent = getItemDisplayName(item);

    selectedItemsContainer.appendChild(itemElement);

    // Show clear button if not already visible
    const clearButton = categoryContainer.querySelector("[data-clear-choices]");
    if (clearButton) {
      clearButton.classList.remove("hidden");
    }
  }

  function setPackageDisabledState(isDisabled) {
    // Check if this is a Mugav package by looking for the validation text element
    const isMugavPackage = document.querySelector('[data-package-validation-required-text]') !== null;

    elements.allItems.forEach((item) => {
      const itemButton = item.querySelector(".select-item-button");

      if (isDisabled) {
        if (isMugavPackage) {
          // For Mugav packages: only hide unselected buttons, show selected ones
          if (itemButton) {
            const isSelected = itemButton.classList.contains("selected");
            if (isSelected) {
              // Show selected buttons but disable interaction for Mugav packages
              itemButton.disabled = false;
              itemButton.classList.remove("btn-deactive");
              itemButton.style.opacity = "";
              itemButton.style.pointerEvents = "none";
            } else {
              // Hide unselected buttons
              itemButton.disabled = true;
              itemButton.classList.add("btn-deactive");
              itemButton.style.opacity = "0";
              itemButton.style.pointerEvents = "none";
            }
          }
          // Don't add package-disabled class to the item for Mugav packages
        } else {
          // For other packages: use original behavior (grey out everything)
          if (itemButton) {
            itemButton.disabled = true;
            itemButton.classList.add("package-disabled");
            itemButton.style.pointerEvents = "none";
          }
          item.classList.add("package-disabled");
        }
      } else {
        // Re-enable button and remove styling (same for all package types)
        if (itemButton) {
          itemButton.disabled = false;
          itemButton.classList.remove("package-disabled");
          itemButton.classList.remove("btn-deactive");
          itemButton.style.opacity = "";
          itemButton.style.pointerEvents = "auto";
        }
        item.classList.remove("package-disabled");
      }
    });

    // Hide/show clear buttons for package selection
    const clearButtons = document.querySelectorAll("[data-clear-choices]");
    clearButtons.forEach((button) => {
      if (isDisabled) {
        // Hide the clear buttons completely for package selection
        button.style.display = "none";
      } else {
        // Show the clear buttons for individual selection
        button.style.display = "";
      }
    });
  }

  function switchToFirstPeakategoriaView() {
    // Check if this is for Mugav residents only
    const isMugavPackage = document.querySelector('[data-package-validation-required-text]') !== null;

    if (!isMugavPackage) {
      return; // Only for Mugav packages
    }

    // Find the first peakategooria trigger button
    const firstPeakategoriaButton = document.querySelector('[data-trigger-builder-view^="view-"][data-list-item-with-cat]');

    if (firstPeakategoriaButton) {
      // Simulate a click on the first peakategooria button to switch views
      firstPeakategoriaButton.click();
    }
  }

  function triggerPackageValidationUpdate() {
    // Dispatch a custom event to trigger package validation update in BaseSelectionManager
    const event = new CustomEvent("packageValidationUpdate");
    document.dispatchEvent(event);
  }
}
